import { json2csv } from "json-2-csv";
import { useCallback, useMemo, useState } from "react";

import { queryClient } from "@providers/QueryClient/queryClient";

import {
  formatEventClassLabel,
  formatLoginTypeLabel,
} from "@utils/activityClassificationLabels";

import {
  type EmployeeHistoricalActivityTimelineQuery,
  type EmployeeHistoricalActivityTimelineQueryVariables,
  useEmployeeHistoricalActivityTimelineQuery,
} from "../HistoricalActivityTimelineCard.g";

type ActivityConnection = NonNullable<
  NonNullable<
    EmployeeHistoricalActivityTimelineQuery["clientEmployee"]
  >["historicalActivities"]
>;
type ActivityNode = NonNullable<
  NonNullable<ActivityConnection["edges"][number]>["node"]
>;

const EVENT_TYPE_LABELS: Record<string, string> = {
  APPLICATION_USE: "Application use",
  CREDENTIAL_USE: "Credential use",
  NOTIFICATION: "Notification",
};

const formatEventType = (eventType: string): string => {
  return EVENT_TYPE_LABELS[eventType] || eventType;
};

type Props = {
  clientId: string;
  clientEmployeeId: string;
  dateRange: [Date | null, Date | null];
  applicationResourceId?: string;
};

export const useEmployeeHistoricalActivityExport = ({
  clientId,
  clientEmployeeId,
  dateRange,
  applicationResourceId,
}: Props) => {
  const [isExporting, setIsExporting] = useState(false);

  const exportData = useCallback(async () => {
    if (isExporting) return;

    setIsExporting(true);
    try {
      const startDate = dateRange[0]?.toISOString();
      const endDate = dateRange[1]?.toISOString();
      const batchSize = 100;
      const allEvents: ActivityNode[] = [];
      let after: string | null | undefined = undefined;
      let hasNextPage = true;

      while (hasNextPage) {
        const variables: EmployeeHistoricalActivityTimelineQueryVariables = {
          clientId,
          clientEmployeeId,
          first: batchSize,
          after,
          startDate,
          endDate,
          ...(applicationResourceId && { applicationResourceId }),
        };

        const pageData: EmployeeHistoricalActivityTimelineQuery =
          await queryClient.fetchQuery({
            queryKey:
              useEmployeeHistoricalActivityTimelineQuery.getKey(variables),
            queryFn:
              useEmployeeHistoricalActivityTimelineQuery.fetcher(variables),
          });

        const connection = pageData.clientEmployee?.historicalActivities;
        const nodes =
          connection?.edges
            ?.map((edge) => edge?.node)
            .filter((node): node is ActivityNode => node != null) ?? [];

        allEvents.push(...nodes);

        hasNextPage = Boolean(connection?.pageInfo.hasNextPage);
        after = connection?.pageInfo.endCursor ?? undefined;

        if (!after) {
          hasNextPage = false;
        }
      }

      if (allEvents.length === 0) {
        throw new Error("No historical activity found for this employee");
      }

      const exportDataArray = allEvents.map((event) => ({
        Action: formatEventType(event.eventType),
        Class: formatEventClassLabel(event.eventClass) || "",
        "Login type": formatLoginTypeLabel(event.loginType) || "",
        Application:
          event.application?.resource?.publicResource?.productName ||
          "System Event",
        Timestamp: event.timestamp,
        Details: event.description || "",
        "Notification type": event.notification?.notificationType || "",
      }));

      const csv = json2csv(exportDataArray);
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `employee-activities-${new Date().toISOString().split("T")[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed:", error);
      throw error;
    } finally {
      setIsExporting(false);
    }
  }, [
    isExporting,
    clientId,
    clientEmployeeId,
    dateRange,
    applicationResourceId,
  ]);

  return useMemo(() => ({ exportData, isExporting }), [exportData, isExporting]);
};
